import { AuthoringTools } from "@/models/constants.model";
import { ProductItem } from "@/models/product.model";
import {
  ContentTemplate,
  MetadataAllowedValues,
  MetadataDefinitionItem
} from "@/models/content-templates.model";
import {
  AuthoredQuestion,
  ExtensionsValue,
  ParsedAuthoredContent,
  XAPI,
  XAPIError,
  XAPIErrors,
  XAPIPreError
} from "@/models/x-api.model";
import { convertToJsonOrNull } from "../utils";
import { isAValidURL } from "../file-helpers";
import { X_API } from "@/constants/xapi.constants";
import { TOOLS } from "@/constants/template.constants";
import {
  MetadataAllocatedValuesItem,
  MetadataInstance,
  ProductSection,
  SectionConcepts,
  SectionFile
} from "@/models/product-section.model";
import {
  ExternalContent,
  ExternalContentAttachment
} from "@/models/api/content.model";
import {
  ChoiceItem,
  InteractionItem,
  QuestionData
} from "@/models/components/question-html-editor.model";
import { INTERACTIONS } from "@/constants/html-editor.constants";

const hasNameSpace = (namespace: string): boolean => {
  return Boolean(namespace);
};
const validateBuildXAPI = async (product: ProductItem): Promise<XAPIErrors> => {
  const contentErrors = getContentErrors(product.sections);
  const contentTemplatesErrors = getContentTemplatesErrors(product);
  const contentWarnings = await getContentWarnings(product.sections);
  return {
    contentErrors,
    contentTemplatesErrors,
    contentWarnings
  };
};

const buildXAPI = async (
  product: ProductItem,
  conceptsArray: string[]
): Promise<undefined | XAPI[]> => {
  let isValid: boolean = true;

  const xAPIJSON: XAPI[] | undefined = await product.sections.reduce(
    async (accP: Promise<Array<XAPI> | undefined>, section: ProductSection) => {
      const acc: XAPI[] | undefined = await accP;
      if (
        hasSectionTypeName(section.type.name) &&
        !TOOLS.isQTITool(section.type.tool_id)
      ) {
        const namespace: string = getNameSpace(product.namespace);
        const mInstances: MetadataInstance[] = section.metadata_instances;
        const cDefinition: ContentTemplate | undefined =
          product.component_definitions.find((a: ContentTemplate) => {
            return a.id === section.type.id;
          });

        if (!cDefinition) {
          isValid = false;
          return;
        }

        if (!cDefinition.xapi_type) {
          isValid = false;
        }
        const extensions: Record<string, ExtensionsValue> | null =
          buildExtensions(
            namespace,
            cDefinition.id,
            mInstances,
            cDefinition.metadata_definition
          );
        if (!!extensions) {
          extensions[
            namespace +
              X_API.EXTENSIONS.contentMetadata +
              X_API.EXTENSIONS.componentName
          ] = cDefinition.name;
        }

        const concepts: string[] = buildExtensionsConcepts(
          section.concepts,
          conceptsArray
        );
        if (concepts.length > 0) {
          if (!!extensions) {
            extensions[X_API.NAMESPACES.adaptemy + X_API.EXTENSIONS.concept] =
              concepts;
          }
        }

        if (TOOLS.isExternalContentTool(section.type.tool_id)) {
          const externalLink: string | null = getExternalLink(section);

          if (!externalLink || !isAValidURL(externalLink)) {
            isValid = false;
          }
          if (!!extensions) {
            extensions[namespace + X_API.EXTENSIONS.pathExternalLink] =
              externalLink;
          }
        }

        if (TOOLS.isHTMLTool(section.type.tool_id)) {
          const html = getHTML(section);

          if (!html) {
            isValid = false;
          }

          if (!!extensions) {
            extensions[namespace + X_API.EXTENSIONS.html] = html;
          }
        }

        if (TOOLS.isQuestionAuthoringTool(section.type.tool_id)) {
          const html = getHTML(section);
          if (!html) {
            isValid = false;
          }

          if (html && getQuestionErrors(section.id, html).length) {
            isValid = false;
          }

          if (!!extensions) {
            extensions[
              namespace +
                X_API.EXTENSIONS.contentMetadata +
                X_API.EXTENSIONS.questionDetails
            ] = buildQuestionDetails(html);
          }
        }

        if (!extensions) {
          isValid = false;
          return acc;
        }
        if (acc) {
          return acc.concat({
            id: namespace + X_API.EXTENSIONS.content + section.id,
            name: section.name,
            definition: {
              type: cDefinition.xapi_type,
              extensions: extensions
            }
          });
        }
        return acc;
      }
      return acc;
    },
    Promise.resolve([])
  );
  if (isValid) {
    return xAPIJSON;
  } else {
    return undefined;
  }
};

const buildExtensions = (
  namespace: string,
  cDefinitionId: number,
  mInstances: MetadataInstance[],
  mDefinition: MetadataDefinitionItem[]
): Record<string, ExtensionsValue> | null => {
  let isValid = true;

  const extensions = mInstances.reduce(
    (acc: Record<string, ExtensionsValue>, mInst: MetadataInstance) => {
      if (!mInst.metadata_definition.url_name) {
        isValid = false;
      }

      const defForInstance: MetadataDefinitionItem | undefined =
        mDefinition.find((m: MetadataDefinitionItem) => {
          return m.id === mInst.metadata_definition.id;
        });
      if (!defForInstance) {
        isValid = false;
      }
      const key: string = buildExtensionsKey(
        namespace,
        mInst.metadata_definition.url_name
      );
      // find duplicate keys
      if (key in acc) {
        isValid = false;
        return acc;
      }
      if (
        isMdAllocatedValueMoreThenOne(
          mInst.metadata_definition.metadata_allocated_values
        )
      ) {
        acc[key] = mInst.metadata_definition.metadata_allocated_values.reduce(
          (acc2: string[], aValue: MetadataAllocatedValuesItem) => {
            const metadataAllocatedValue: MetadataAllowedValues | undefined =
              defForInstance
                ? defForInstance.metadata_allowed_values.find(
                    (d: MetadataAllowedValues) =>
                      d.id === aValue.metadata_allowed_value_id
                  )
                : undefined;
            const valueName = metadataAllocatedValue
              ? metadataAllocatedValue.display_name
              : "";
            return acc2.concat(valueName);
          },
          []
        );
      } else if (
        isMdAllocatedValueIsEqualToOne(
          mInst.metadata_definition.metadata_allocated_values
        )
      ) {
        const allowedValue = defForInstance
          ? defForInstance.metadata_allowed_values.find(
              (value: MetadataAllowedValues) => {
                return mInst.metadata_definition.metadata_allocated_values.find(
                  (aValue: MetadataAllocatedValuesItem) => {
                    return aValue.metadata_allowed_value_id === value.id;
                  }
                );
              }
            )
          : undefined;
        acc[key] = allowedValue ? allowedValue.display_name : "";
      } else if (
        isMdAllocatedValueAreText(
          mInst.authored_text,
          mInst.metadata_definition.metadata_allocated_values
        )
      ) {
        acc[key] = mInst.authored_text;
      } else {
        isValid = false;
      }
      return acc;
    },
    {}
  );

  if (isValid) {
    return extensions;
  } else {
    return null;
  }
};

const buildExtensionsConcepts = (
  sectionConcepts: SectionConcepts[],
  concepts: string[]
): string[] => {
  if (concepts.length > 0) {
    return sectionConcepts
      .filter(obj => concepts.indexOf(obj.concept_guid) !== -1)
      .map(s => {
        return s.concept_guid;
      });
  } else {
    return [];
  }
};

const buildExternalLink = (
  authoredContent: string,
  toolId: number
): string | null => {
  let link = null;

  const authoredContentJson: ParsedAuthoredContent =
    convertToJsonOrNull(authoredContent);

  if (TOOLS.isExternalContentTool(toolId) && authoredContentJson) {
    const authTool = TOOLS.TYPES.find((t: AuthoringTools) => t.id === toolId);
    const authToolKey: string | null = authTool ? authTool.key : null;

    link =
      authToolKey && authoredContentJson[authToolKey]
        ? authoredContentJson[authToolKey]
        : findFirstValue(authoredContentJson.extensions, ([key]) =>
            key.includes(X_API.EXTENSIONS.pathExternalLink)
          );
  }
  return link;
};

const buildQuestionDetails = (html: string | null): AuthoredQuestion | null => {
  if (!html) {
    return null;
  }
  const parsedDoc = JSON.parse(html);
  const interactions = parsedDoc.interactions.length
    ? parsedDoc.interactions.map((int: InteractionItem) => {
        const intData = {
          guid: int.id,
          type: int.type
        };
        if (INTERACTIONS.TYPES.isChoiceType(int.type)) {
          const expectedResponses: string[] = [];
          const choices = int.choices.length
            ? int.choices.map((choice: ChoiceItem) => {
                if (choice.isCorrect) {
                  expectedResponses.push(choice.id);
                }
                return {
                  guid: choice.id,
                  display: {
                    html: choice.html
                  },
                  feedback: {
                    display: {
                      html: choice.solution
                    }
                  },
                  hint: {
                    display: {
                      html: choice.hint
                    }
                  }
                };
              })
            : [];
          return {
            ...intData,
            expectedResponses,
            choices
          };
        }
        return intData;
      })
    : [];
  return {
    metadata: {},
    metrics: {},
    display: {
      html: parsedDoc.question
    },
    maxAttempts: 2,
    interactions
  };
};

const findFirstValue = (
  object: string,
  callback: (entry: [string, string]) => boolean
): string => {
  const firstMatchEntry: [string, string] | undefined =
    Object.entries(object).find(callback);
  if (firstMatchEntry) {
    return firstMatchEntry[1]; // extract value from entry
  }
  return "";
};

const getExternalLinkById = (
  section: ProductSection,
  authoringContent: ExternalContent
): string | null => {
  const currentFile = section.files.find((file: SectionFile): boolean => {
    return (
      file.id ===
      (authoringContent.externalContent as ExternalContentAttachment)
        .attachmentId
    );
  });
  if (currentFile) {
    return JSON.stringify({
      externalContent: currentFile.location
    });
  }
  return null;
};

function getContentErrors(sections: ProductSection[]): XAPIError[] {
  return mergeErrorsArray([
    ...getExternalLinkErrors(sections),
    ...getContentMetadataErrors(sections),
    ...getHTMLErrors(sections),
    ...getAuthoredQuestionsErrors(sections)
  ]);
}

function getHTMLErrors(sections: ProductSection[]): XAPIPreError[] {
  return sections.reduce((acc: XAPIPreError[], section: ProductSection) => {
    if (hasSectionTypeName(section.type.name)) {
      if (TOOLS.isHTMLTool(section.type.tool_id)) {
        const html = getHTML(section);
        if (html) {
          return acc;
        }
        return [
          ...acc,
          { sectionId: section.id, error: X_API.ERRORS.noHTMLDocumentAuthored }
        ];
      }
      return acc;
    }
    return acc;
  }, []);
}

function getChoiceErrors(
  sectionId: number,
  choices: ChoiceItem[]
): XAPIPreError[] {
  const errors: XAPIPreError[] = [];
  if (choices.length < 2) {
    errors.push({
      sectionId: sectionId,
      error: X_API.ERRORS.lessThan2Choices
    });
  } else {
    const isCorrectAnswer = choices.some((choice: ChoiceItem) => {
      return choice.isCorrect;
    });
    if (!isCorrectAnswer) {
      errors.push({
        sectionId: sectionId,
        error: X_API.ERRORS.noCorrectAnswers
      });
    }
    const isChoiceEmpty = choices.some((choice: ChoiceItem) => {
      return !choice.html || choice.html === "<p></p>";
    });
    if (isChoiceEmpty) {
      errors.push({
        sectionId: sectionId,
        error: X_API.ERRORS.emptyChoices
      });
    }
  }
  return errors;
}

function getInteractionErrors(
  sectionId: number,
  interactions: InteractionItem[]
): XAPIPreError[] {
  let errors: XAPIPreError[] = [];
  if (!interactions.length) {
    errors.push({
      sectionId: sectionId,
      error: X_API.ERRORS.noInteractions
    });
  } else {
    interactions.forEach((int: InteractionItem) => {
      if (!int.type) {
        errors.push({
          sectionId: sectionId,
          error: X_API.ERRORS.interactionTypeNotSelected
        });
      } else if (INTERACTIONS.TYPES.isChoiceType(int.type)) {
        errors = errors.concat(getChoiceErrors(sectionId, int.choices));
      }
    });
  }
  return errors;
}

function getQuestionErrors(sectionId: number, html: string): XAPIPreError[] {
  let errors: XAPIPreError[] = [];
  const parsedDoc: QuestionData = JSON.parse(html);
  if (!parsedDoc.question || parsedDoc.question === "<p></p>") {
    errors.push({
      sectionId: sectionId,
      error: X_API.ERRORS.emptyQuestion
    });
  }
  const interactionErrors = getInteractionErrors(
    sectionId,
    parsedDoc.interactions
  );
  errors = errors.concat(interactionErrors);
  return errors;
}

function getAuthoredQuestionsErrors(
  sections: ProductSection[]
): XAPIPreError[] {
  return sections.reduce((acc: XAPIPreError[], section: ProductSection) => {
    if (
      hasSectionTypeName(section.type.name) &&
      TOOLS.isQuestionAuthoringTool(section.type.tool_id)
    ) {
      const html = getHTML(section);
      if (!html) {
        return [
          ...acc,
          { sectionId: section.id, error: X_API.ERRORS.noQuestionAuthored }
        ];
      }
      const questionErrors = getQuestionErrors(section.id, html);
      return [...acc, ...questionErrors];
    }
    return acc;
  }, []);
}

function getHTML(section: ProductSection): string | null {
  return section.document ?? null;
}

function getExternalLinkErrors(sections: ProductSection[]): XAPIPreError[] {
  return sections.reduce((acc: XAPIPreError[], section: ProductSection) => {
    if (hasSectionTypeName(section.type.name)) {
      if (TOOLS.isExternalContentTool(section.type.tool_id)) {
        const extLink = getExternalLink(section);
        const errorOrNull = validateExternalLink(extLink);
        if (errorOrNull === null) {
          return acc;
        }
        return [...acc, { sectionId: section.id, error: errorOrNull }];
      }
      return acc;
    }
    return acc;
  }, []);
}

function getExternalLink(section: ProductSection): string | null {
  let authContent: string | null;
  const authContentAsAnObject: ExternalContent = JSON.parse(
    section.authored_content
  );
  if (
    !!section.authored_content &&
    authContentAsAnObject.externalContent &&
    authContentAsAnObject.externalContent.hasOwnProperty("attachmentId")
  ) {
    authContent = getExternalLinkById(section, authContentAsAnObject);
  } else {
    authContent = section.authored_content;
  }
  return authContent
    ? buildExternalLink(authContent, section.type.tool_id)
    : null;
}

function validateExternalLink(externalLink: string | null): string | null {
  if (!externalLink || !isAValidURL(externalLink)) {
    return !externalLink
      ? X_API.ERRORS.externalLinkNotSet
      : !isAValidURL(externalLink)
      ? X_API.ERRORS.invalidExternalLink
      : "";
  }
  return null;
}

function getContentTemplatesErrors(product: ProductItem): XAPIError[] {
  return mergeErrorsArray([
    ...getDefinitionXapiTypeErrors(product),
    ...getDefinitionUrlError(product),
    ...getDefinitionDuplicateKeyError(product)
  ]);
}

async function getContentWarnings(sections: any[]): Promise<[] | XAPIError[]> {
  return mergeErrorsArray([...getQuestionWarnings(sections)]);
}

function getQuestionWarnings(sections: ProductSection[]): XAPIPreError[] {
  return sections.reduce((acc: XAPIPreError[], section: ProductSection) => {
    if (TOOLS.isQuestionAuthoringTool(section.type.tool_id)) {
      const html = getHTML(section);
      if (!html) {
        return acc;
      }
      const parsedDoc: QuestionData = JSON.parse(html);
      const errors: XAPIPreError[] = [];
      if (!parsedDoc.interactions.length) {
        return acc;
      } else {
        parsedDoc.interactions.forEach((int: InteractionItem) => {
          if (INTERACTIONS.TYPES.isChoiceType(int.type)) {
            const isNoHint = int.choices.some((choice: ChoiceItem) => {
              return !choice.hint || choice.hint === "<p></p>";
            });
            if (isNoHint) {
              errors.push({
                sectionId: section.id,
                error: X_API.ERRORS.hintNotAdded
              });
            }
            const isNoSolution = int.choices.some((choice: ChoiceItem) => {
              return !choice.solution || choice.solution === "<p></p>";
            });
            if (isNoSolution) {
              errors.push({
                sectionId: section.id,
                error: X_API.ERRORS.solutionNotAdded
              });
            }
          }
        });
      }
      return [...acc, ...errors];
    }
    return acc;
  }, []);
}

function getContentMetadataErrors(sections: ProductSection[]): XAPIPreError[] {
  return sections.reduce((accErr: XAPIPreError[], section: ProductSection) => {
    if (hasSectionTypeName(section.type.name)) {
      const mdInstanceError = section.metadata_instances.reduce(
        (acc: XAPIPreError[], mInst: MetadataInstance) => {
          if (
            isMdAllocatedValueMoreThenOne(
              mInst.metadata_definition.metadata_allocated_values
            ) ||
            isMdAllocatedValueIsEqualToOne(
              mInst.metadata_definition.metadata_allocated_values
            ) ||
            isMdAllocatedValueAreText(
              mInst.authored_text,
              mInst.metadata_definition.metadata_allocated_values
            )
          ) {
            return acc;
          } else {
            const metadataError = `${X_API.ERRORS.metadataValueMissing}: "${mInst.metadata_definition.name}"`;
            return [...acc, { sectionId: section.id, error: metadataError }];
          }
        },
        []
      );
      return [...accErr, ...mdInstanceError];
    }
    return accErr;
  }, []);
}

function getDefinitionXapiTypeErrors(product: ProductItem): XAPIPreError[] {
  return product.sections.reduce(
    (acc: XAPIPreError[], section: ProductSection) => {
      if (hasSectionTypeName(section.type.name)) {
        const cDefinition = product.component_definitions.find(
          a => a.id === section.type.id
        );

        // should we throw an error?
        if (!cDefinition) {
          return acc;
        }
        if (!cDefinition.xapi_type) {
          const typeError = `${X_API.ERRORS.xApiTypeNotSet}: "${cDefinition.name}"`;
          return [...acc, { sectionId: cDefinition.id, error: typeError }];
        }
        return acc;
      }
      return acc;
    },
    []
  );
}

function getDefinitionUrlError(product: ProductItem): XAPIPreError[] {
  return product.sections.reduce(
    (accErr: XAPIPreError[], section: ProductSection) => {
      if (hasSectionTypeName(section.type.name)) {
        const mInstances = section.metadata_instances;
        const cDefinition = product.component_definitions.find(
          a => a.id === section.type.id
        );
        let mInstancesErrors;
        if (cDefinition) {
          mInstancesErrors = mInstances.reduce(
            (acc: XAPIPreError[], mInst: MetadataInstance) => {
              if (!mInst.metadata_definition.url_name) {
                const URLError = `${X_API.ERRORS.metadataFriendlyURlNotSet}: "${mInst.metadata_definition.name}"`;
                return [...acc, { sectionId: cDefinition.id, error: URLError }];
              }
              return acc;
            },
            []
          );
        } else {
          // should we throw an error?
          return accErr;
        }
        return [...accErr, ...mInstancesErrors];
      }
      return accErr;
    },
    []
  );
}

function getDefinitionDuplicateKeyError(product: ProductItem): XAPIPreError[] {
  const namespace = getNameSpace(product.namespace);
  return product.sections.reduce(
    (accErr: XAPIPreError[], section: ProductSection) => {
      if (hasSectionTypeName(section.type.name)) {
        const mInstances = section.metadata_instances;
        const cDefinition = product.component_definitions.find(
          (a: ContentTemplate) => a.id === section.type.id
        );
        const instanceKeys: string[] = [];
        const mInstancesKeyErrors = mInstances.reduce(
          (acc: XAPIPreError[], mInst: MetadataInstance) => {
            const key: string =
              buildExtensionsKey(
                namespace,
                mInst.metadata_definition.url_name
              ) || "";

            // find duplicate keys
            if (hasKeyInArray(key, instanceKeys) && cDefinition) {
              const duplicateError = `${X_API.ERRORS.duplicateMetadataFriendlyUrl}: "${mInst.metadata_definition.name}"`;
              return [
                ...acc,
                { sectionId: cDefinition.id, error: duplicateError }
              ];
            }
            instanceKeys.push(key);
            return acc;
          },
          []
        );
        return [...accErr, ...mInstancesKeyErrors];
      }
      return accErr;
    },
    []
  );
}

function mergeErrorsArray(errorsArray: XAPIPreError[]): XAPIError[] {
  const errors: XAPIError[] = [];
  for (let i = 0; i < errorsArray.length; i++) {
    const isItemInErrorsArray = errors.some(
      (item: XAPIError): boolean => item.sectionId === errorsArray[i].sectionId
    );
    if (!isItemInErrorsArray) {
      errors.push({
        sectionId: errorsArray[i].sectionId,
        errors: [errorsArray[i].error]
      });
    } else {
      const currentItemInErrorsArray = errors.find(
        (item: XAPIError): boolean =>
          item.sectionId === errorsArray[i].sectionId
      );
      if (currentItemInErrorsArray) {
        if (!currentItemInErrorsArray.errors.includes(errorsArray[i].error)) {
          currentItemInErrorsArray.errors.push(errorsArray[i].error);
        }
      }
    }
  }
  return errors;
}

function isMdAllocatedValueMoreThenOne(
  allocatedValues: MetadataAllocatedValuesItem[]
): boolean {
  return allocatedValues.length > 1;
}

function isMdAllocatedValueIsEqualToOne(
  allocatedValues: MetadataAllocatedValuesItem[]
): boolean {
  return allocatedValues.length === 1;
}

function isMdAllocatedValueAreText(
  text: string,
  allocatedValues: MetadataAllocatedValuesItem[]
): boolean {
  return !!text && allocatedValues.length === 0;
}

function getNameSpace(namespace: string): string {
  return namespace.replace(/\/$/, "");
}

function hasKeyInArray(key: string, arrayToTest: string[]): boolean {
  return arrayToTest.includes(key);
}

function buildExtensionsKey(namespace: string, urlName: string): string {
  return namespace + X_API.EXTENSIONS.contentMetadata + urlName;
}

function hasSectionTypeName(name: string): boolean {
  return !X_API.SECTIONS_TO_SKIP.includes(name);
}

export {
  hasNameSpace,
  buildXAPI,
  buildExtensions,
  buildExtensionsConcepts,
  buildExternalLink,
  validateBuildXAPI,
  getContentErrors,
  mergeErrorsArray,
  getContentTemplatesErrors,
  getContentWarnings,
  buildQuestionDetails
};
